import { useState } from 'react';
import { SelectField } from '../../components/ui/SelectField';
// ⚠️ Same file name as this module's own component, hence the alias: the shared
// one is the single-popover start+end CALENDAR (built for the Community inbox);
// this one is the reporting PRESET control that wraps it.
import { DateRangePicker as CalendarRangePicker } from '../../components/ui/DateRangePicker';
import type { DateRange } from '../../lib/reportingDateRange';
import type { ReportingDatePreset } from '../../types/reporting';

const PRESETS: Array<{ value: ReportingDatePreset; label: string }> = [
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  // "This …" calendar-to-date presets, grouped shortest → longest, with the
  // whole-previous-month window sitting next to This month (the pair a user
  // flips between when comparing).
  { value: 'this_week', label: 'This week' },
  { value: 'this_month', label: 'This month' },
  { value: 'last_month', label: 'Last month' },
  { value: 'this_quarter', label: 'This quarter' },
  { value: 'this_year', label: 'This year' },
  // Rolling windows, shortest → longest.
  { value: 'past_7', label: 'Past 7 days' },
  { value: 'past_14', label: 'Past 14 days' },
  { value: 'past_30', label: 'Past 30 days' },
  { value: 'past_60', label: 'Past 60 days' },
  { value: 'past_90', label: 'Past 90 days' },
  { value: 'past_180', label: 'Past 180 days' },
  { value: 'past_365', label: 'Past 365 days' },
  { value: 'custom', label: 'Custom…' },
];

/**
 * The reporting date-range control, shared by BOTH dashboards.
 *
 * The presets are a DROPDOWN rather than the row of chips they used to be:
 * fourteen always-visible chips wrapped across the top of the page and read as
 * clutter, while only one of them is ever active.
 *
 * Custom uses the shared single-popover calendar (the Community inbox's), so a
 * start and an end are picked in one place instead of in two separate date
 * inputs. The page still resolves the effective {from, to} via presetToRange.
 */
export function DateRangePicker({
  preset,
  onPresetChange,
  custom,
  onCustomChange,
}: {
  preset: ReportingDatePreset;
  onPresetChange: (p: ReportingDatePreset) => void;
  custom: DateRange;
  onCustomChange: (c: DateRange) => void;
}) {
  // ⚠️ THE HALF-OPEN RANGE LIVES HERE, not in the committed value, and it is not
  // optional polish. The calendar fires on the FIRST click with `to: null`, and
  // it decides "extend vs start over" by looking at whether `to` is already set
  // — so writing a placeholder end back into it (say, from === to) would make
  // the second click restart the selection instead of completing it, leaving a
  // range that can never be finished. Holding the in-progress pick locally also
  // means the page's range only changes once a real range exists, so the whole
  // dashboard never refetches against a half-picked window.
  const [pending, setPending] = useState<{ from: string } | null>(null);

  const onCalendarChange = (from: string | undefined, to: string | undefined) => {
    if (from && to) {
      setPending(null);
      onCustomChange({ from, to });
    } else if (from) {
      setPending({ from });
    } else {
      // The calendar's Clear. A reporting range is required, so this abandons
      // the in-progress pick and keeps the committed one rather than blanking
      // the page.
      setPending(null);
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-2">
      <SelectField
        size="sm"
        aria-label="Date range"
        value={preset}
        onValueChange={(v) => onPresetChange(v as ReportingDatePreset)}
        options={PRESETS}
        className="min-w-[9rem]"
      />
      {preset === 'custom' && (
        <div className="min-w-[15rem]">
          <CalendarRangePicker
            ariaLabel="Custom date range"
            from={pending ? pending.from : custom.from}
            to={pending ? null : custom.to}
            onChange={onCalendarChange}
          />
        </div>
      )}
    </div>
  );
}
